// dependencies
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { Breadcrumb, Breadcrumbs as PyBreadcrumbs } from "@performyard/py-components";

// components
import PageTitle from "../../../components/PageTitle";
import Progress from "../../../components/ProgressTimeline";
// config
import Config from "./Config";

// hooks
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";

// actions
import { changePage, initialize } from "./actions";

// selectors
import { getAppWidth, getUserCompanyName } from "../../../selectors/app";
import { getCurrentPage, getDeactivatedActors, getHasUnsavedChanges } from "./selectors";

// utils
import { parseWorkflowsOrigin } from "../../../utils/workflowsBreadcrumbs";

// steps
import CreateWorkflow from "./steps/CreateForm";
import Finishing from "./steps/Finishing";
import TemplateAssigning from "./steps/TemplateAssigning";
import UserSelection from "./steps/UserSelection";

const NAVIGATION_WARNING_MESSAGE =
	"The page is asking you to confirm that you want to leave - information you've entered may not be saved.";

const LaunchWorkflow = () => {
	const dispatch = useDispatch<any>();
	const location = useLocation();
	const isNewNav = useIsNewNavigation();
	// Capture the origin once: advancing the wizard rewrites the query string and would drop `from`.
	const [originSearch] = useState(() => location.search);
	const appWidth = useSelector(getAppWidth);
	const companyName = useSelector(getUserCompanyName);
	const currentPage = useSelector(getCurrentPage);
	const deactivatedActors = useSelector(getDeactivatedActors);
	const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
	const [isLaunching, setIsLaunching] = useState(false);

	const { setIsSafe, setIsUnsafe } = useUnsavedChanges({
		message: NAVIGATION_WARNING_MESSAGE,
		blockSearchParamChanges: false,
		disabled: isLaunching,
	});

	useEffect(() => {
		dispatch(initialize());
	}, [dispatch]);

	useEffect(() => {
		if (hasUnsavedChanges) {
			setIsUnsafe();
		} else {
			setIsSafe();
		}
	}, [hasUnsavedChanges, setIsSafe, setIsUnsafe]);

	const handleBeforeLaunch = () => {
		setIsLaunching(true);
		setIsSafe();
	};

	const handleLaunchFailed = () => {
		setIsLaunching(false);
	};

	const handleNextButton = () => {
		if (currentPage === 2 && deactivatedActors) {
			dispatch(changePage(currentPage + 1));
		} else if (currentPage !== 2 || (currentPage === 2 && !deactivatedActors)) {
			dispatch(changePage(currentPage + 1));
		}
	};

	const handleBackButton = () => {
		dispatch(changePage(currentPage - 1));
	};

	const handleDotClick = (page: number) => {
		dispatch(changePage(page));
	};

	let content: React.ReactNode = "";

	if (currentPage === 0) {
		content = <CreateWorkflow />;
	} else if (currentPage === 1) {
		content = <UserSelection />;
	} else if (currentPage === 2) {
		content = <TemplateAssigning />;
	} else if (currentPage === 3) {
		content = <Finishing onBeforeLaunch={handleBeforeLaunch} onLaunchFailed={handleLaunchFailed} />;
	}

	const formIsComplete = true;

	const renderBreadcrumbs = () => {
		if (!isNewNav) return null;
		const { from, label } = parseWorkflowsOrigin(originSearch);
		const crumbs: Breadcrumb[] = [
			{ pageId: "breadcrumb-workflows-origin", name: label, href: from },
			{ pageId: "breadcrumb-launch-workflow-current", name: "Launch Workflow", href: location.pathname },
		];
		return (
			<div className="pb-4">
				<PyBreadcrumbs items={crumbs} />
			</div>
		);
	};

	return (
		<>
			<PageTitle title={`Launch Workflow${companyName ? ` - ${companyName}` : ""} - PerformYard`} />
			<section
				className="page-wrapper"
				style={{
					margin: `0 ${Config.pageWrapperSpacing}px`,
					paddingTop: Config.pageWrapperSpacing,
					paddingBottom: "80px",
				}}
			>
				{renderBreadcrumbs()}
				<section className="mx-auto w-[500px] border-b border-solid border-b-[var(--py-neutral-1)] pb-2.5">
					<Progress
						onClick={handleDotClick}
						height={30}
						pageCount={4}
						currentPage={currentPage}
						width={300}
					/>
				</section>

				{content}

				<div
					style={{
						width: appWidth - 20,
						left: 10,
						bottom: ((formIsComplete || currentPage > 0) && currentPage) !== 3 ? 10 : -100,
					}}
					className="fixed bottom-0 left-0 h-[60px] w-full rounded bg-primary-5 transition-all duration-150 ease-out"
				>
					{currentPage !== 0 ? (
						<button
							onClick={handleBackButton}
							className="absolute top-3 left-2.5 h-9 w-[100px] cursor-pointer rounded-sm border-0 bg-black/30 px-3 py-2 text-white shadow-none hover:bg-black/40"
						>
							Back
						</button>
					) : (
						""
					)}
					<button
						disabled={currentPage === 2 && deactivatedActors}
						onClick={handleNextButton}
						className="absolute top-3 right-2.5 h-9 w-[100px] cursor-pointer rounded-sm border-0 bg-white px-3 py-2 font-medium text-primary-5 shadow-none"
					>
						Next
					</button>
				</div>
			</section>
		</>
	);
};

export default LaunchWorkflow;
